import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, Spinner, Label, Checkbox, Textarea, Input, Switch,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  RadioGroup, RadioGroupItem,
} from '@agion/ui'
import { Rocket, Info, Key, ChevronDown, Trash2, RotateCcw } from 'lucide-react'
import { createOperation } from '@/features/operation/infra_operation.service'
import { generateBootstrapToken } from './infra_governance.service'
import { listChartVersions } from '@/features/chart/infra_chart.service'
import { listSshKeys } from '@/features/secondary/infra_ssh_key.service'
import type { ChartVersion } from '@/features/chart/infra_chart.service'
import type { SshKey } from '@/features/secondary/infra_ssh_key.service'
import type { CloudEnvironment, OperationType } from '@/core/infra_types'

type EdgeRuntimeMode = 'upgrade' | 'reinstall' | 'uninstall'

// JSON format hint for the helm value overrides textarea — not user prose.
const VALUES_OVERRIDE_PLACEHOLDER = '{"convergenceJobs.imagePullPolicy": "Never"}'

const MODE_TO_OPERATION: Record<EdgeRuntimeMode, OperationType> = {
  upgrade: 'helm_install',
  reinstall: 'helm_reinstall',
  uninstall: 'helm_uninstall',
}

// First-class override field definitions
interface FirstClassOverrides {
  flywayAgionDigest: string
  flywayIntelligenceDigest: string
  agentHostTag: string
  missionRunnerTag: string
  agionMcpTag: string
  intelligenceZoneTag: string
  intelligenceIngestionTag: string
  channelGatewayTag: string
  replicas: string
  vpnEnabled: boolean | null
}

const EMPTY_OVERRIDES: FirstClassOverrides = {
  flywayAgionDigest: '',
  flywayIntelligenceDigest: '',
  agentHostTag: '',
  missionRunnerTag: '',
  agionMcpTag: '',
  intelligenceZoneTag: '',
  intelligenceIngestionTag: '',
  channelGatewayTag: '',
  replicas: '',
  vpnEnabled: null,
}

interface Props {
  open: boolean
  environment: CloudEnvironment
  onClose: () => void
  onSuccess: (operationId: string) => void
  initialMode?: EdgeRuntimeMode
}

export function EdgeRuntimeDialog({ open, environment, onClose, onSuccess, initialMode = 'upgrade' }: Props) {
  const { t } = useTranslation(['pages'])
  const [mode, setMode] = useState<EdgeRuntimeMode>(initialMode)

  const cfg = (environment.config ?? {}) as Record<string, unknown>
  const defaultChartVersion = String(cfg['chartVersion'] ?? cfg['chart_version'] ?? '')
  const needsSshKey = ['k3s_bare', 'k8s_edge', 'dc_ha'].includes(environment.deploymentModel)
    && !!(cfg['bastionIp'] ?? cfg['bastion_ip'])

  const [chartVersion, setChartVersion] = useState(defaultChartVersion)
  const [chartVersions, setChartVersions] = useState<ChartVersion[]>([])
  const [isLoadingVersions, setIsLoadingVersions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sshKeys, setSshKeys] = useState<SshKey[]>([])
  const [selectedSshKeyId, setSelectedSshKeyId] = useState<string>('')
  const [deleteAllPvcs, setDeleteAllPvcs] = useState(false)
  const [recoveryMode, setRecoveryMode] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [valuesOverrideText, setValuesOverrideText] = useState('')
  const [valuesOverrideError, setValuesOverrideError] = useState<string | null>(null)

  // First-class override fields state
  const [firstClassOverrides, setFirstClassOverrides] = useState<FirstClassOverrides>(EMPTY_OVERRIDES)

  useEffect(() => {
    if (open) {
      setMode(initialMode)
      setDeleteAllPvcs(false)
      setRecoveryMode(false)
      setShowAdvanced(false)
      setValuesOverrideText('')
      setValuesOverrideError(null)
      setFirstClassOverrides(EMPTY_OVERRIDES)
      setError(null)
      setIsLoadingVersions(true)
      setSelectedSshKeyId(String(cfg['sshKeyId'] ?? cfg['ssh_key_id'] ?? ''))
      const fetches: Promise<unknown>[] = [
        listChartVersions()
          .then(versions => {
            setChartVersions(versions)
            const latest = versions[0]?.version
            setChartVersion(latest ?? defaultChartVersion)
          })
          .catch(() => {
            setChartVersions([])
            setChartVersion(defaultChartVersion)
            setError('Failed to load chart versions')
          }),
      ]
      if (needsSshKey) {
        fetches.push(
          listSshKeys().then(resp => setSshKeys(resp.data ?? [])).catch(() => {
            setSshKeys([])
            setError(prev => prev ? `${prev}. Failed to load SSH keys` : 'Failed to load SSH keys')
          })
        )
      }
      Promise.all(fetches).finally(() => setIsLoadingVersions(false))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cfg is derived from environment (stable ref while open)
  }, [open, defaultChartVersion, needsSshKey, initialMode])

  const parseValuesOverride = (): Record<string, unknown> | null => {
    const text = valuesOverrideText.trim()
    if (!text) return null
    try {
      const parsed = JSON.parse(text)
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        return null
      }
      return parsed as Record<string, unknown>
    } catch {
      return null
    }
  }

  const handleValuesChange = (text: string) => {
    setValuesOverrideText(text)
    if (!text.trim()) {
      setValuesOverrideError(null)
      return
    }
    try {
      const parsed = JSON.parse(text)
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        setValuesOverrideError('Invalid JSON: must be an object, not an array or primitive')
      } else {
        setValuesOverrideError(null)
      }
    } catch {
      setValuesOverrideError('Invalid JSON')
    }
  }

  // Build combined values_override object (first-class wins over advanced JSON)
  const buildValuesOverride = (): Record<string, unknown> | null => {
    const advancedJson = parseValuesOverride() ?? {}

    // Start with advanced JSON, then overlay first-class fields (first-class wins)
    const combined: Record<string, unknown> = { ...advancedJson }

    // Flyway digests
    if (firstClassOverrides.flywayAgionDigest.trim()) {
      combined['image.flywayAgion.digest'] = firstClassOverrides.flywayAgionDigest.trim()
    }
    if (firstClassOverrides.flywayIntelligenceDigest.trim()) {
      combined['image.flywayIntelligence.digest'] = firstClassOverrides.flywayIntelligenceDigest.trim()
    }

    // Binary tags
    if (firstClassOverrides.agentHostTag.trim()) {
      combined['image.agentHost.tag'] = firstClassOverrides.agentHostTag.trim()
    }
    if (firstClassOverrides.missionRunnerTag.trim()) {
      combined['image.missionRunner.tag'] = firstClassOverrides.missionRunnerTag.trim()
    }
    if (firstClassOverrides.agionMcpTag.trim()) {
      combined['image.agionMcp.tag'] = firstClassOverrides.agionMcpTag.trim()
    }
    if (firstClassOverrides.intelligenceZoneTag.trim()) {
      combined['image.intelligenceZone.tag'] = firstClassOverrides.intelligenceZoneTag.trim()
    }
    if (firstClassOverrides.intelligenceIngestionTag.trim()) {
      combined['image.intelligenceIngestion.tag'] = firstClassOverrides.intelligenceIngestionTag.trim()
    }
    if (firstClassOverrides.channelGatewayTag.trim()) {
      combined['image.channelGateway.tag'] = firstClassOverrides.channelGatewayTag.trim()
    }

    // StatefulSet replicas
    if (firstClassOverrides.replicas.trim()) {
      const numReplicas = parseInt(firstClassOverrides.replicas.trim(), 10)
      if (!isNaN(numReplicas) && numReplicas > 0) {
        combined['statefulSet.replicas'] = numReplicas
      }
    }

    // VPN enabled (only include if explicitly set)
    if (firstClassOverrides.vpnEnabled !== null) {
      combined['vpn.enabled'] = firstClassOverrides.vpnEnabled
    }

    return Object.keys(combined).length > 0 ? combined : null
  }

  // Get active overrides for preview
  const getActiveOverrides = (): Array<{ path: string; value: string }> => {
    const overrides: Array<{ path: string; value: string }> = []

    if (firstClassOverrides.flywayAgionDigest.trim()) {
      overrides.push({ path: 'image.flywayAgion.digest', value: firstClassOverrides.flywayAgionDigest.trim() })
    }
    if (firstClassOverrides.flywayIntelligenceDigest.trim()) {
      overrides.push({ path: 'image.flywayIntelligence.digest', value: firstClassOverrides.flywayIntelligenceDigest.trim() })
    }
    if (firstClassOverrides.agentHostTag.trim()) {
      overrides.push({ path: 'image.agentHost.tag', value: firstClassOverrides.agentHostTag.trim() })
    }
    if (firstClassOverrides.missionRunnerTag.trim()) {
      overrides.push({ path: 'image.missionRunner.tag', value: firstClassOverrides.missionRunnerTag.trim() })
    }
    if (firstClassOverrides.agionMcpTag.trim()) {
      overrides.push({ path: 'image.agionMcp.tag', value: firstClassOverrides.agionMcpTag.trim() })
    }
    if (firstClassOverrides.intelligenceZoneTag.trim()) {
      overrides.push({ path: 'image.intelligenceZone.tag', value: firstClassOverrides.intelligenceZoneTag.trim() })
    }
    if (firstClassOverrides.intelligenceIngestionTag.trim()) {
      overrides.push({ path: 'image.intelligenceIngestion.tag', value: firstClassOverrides.intelligenceIngestionTag.trim() })
    }
    if (firstClassOverrides.channelGatewayTag.trim()) {
      overrides.push({ path: 'image.channelGateway.tag', value: firstClassOverrides.channelGatewayTag.trim() })
    }
    if (firstClassOverrides.replicas.trim()) {
      overrides.push({ path: 'statefulSet.replicas', value: firstClassOverrides.replicas.trim() })
    }
    if (firstClassOverrides.vpnEnabled !== null) {
      overrides.push({ path: 'vpn.enabled', value: String(firstClassOverrides.vpnEnabled) })
    }

    // Add advanced JSON entries that are not overridden by first-class fields
    const advancedJson = parseValuesOverride()
    if (advancedJson) {
      const firstClassPaths = new Set(overrides.map(o => o.path))
      for (const [key, value] of Object.entries(advancedJson)) {
        if (!firstClassPaths.has(key)) {
          overrides.push({ path: key, value: String(value) })
        }
      }
    }

    return overrides
  }

  const hasValuesError = valuesOverrideText.trim() !== '' && valuesOverrideError !== null
  const isUninstall = mode === 'uninstall'
  const isReinstall = mode === 'reinstall'
  const isUpgrade = mode === 'upgrade'

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      if (isUninstall) {
        const op = await createOperation({
          environmentId: environment.id,
          operationType: MODE_TO_OPERATION[mode],
          parameters: { release_name: 'agion-edge' },
        })
        onSuccess(op.id)
        onClose()
      } else {
        const groupId = (cfg['hostGroupId'] ?? cfg['host_group_id']) as string | undefined
        let bootstrap: Awaited<ReturnType<typeof generateBootstrapToken>> | null = null
        if (groupId) {
          try {
            bootstrap = await generateBootstrapToken(groupId)
          } catch (err) {
            const detail = err instanceof Error ? err.message : String(err)
            setError(`Failed to generate bootstrap token: ${detail}`)
            setIsSubmitting(false)
            return
          }
        }
        const valuesOverride = buildValuesOverride()
        const op = await createOperation({
          environmentId: environment.id,
          operationType: MODE_TO_OPERATION[mode],
          parameters: {
            release_name: 'agion-edge',
            chart_version: chartVersion,
            ...(bootstrap ? { bootstrap_token: bootstrap.bootstrapToken } : {}),
            ...(selectedSshKeyId ? { ssh_key_id: selectedSshKeyId } : {}),
            ...(isReinstall && deleteAllPvcs ? { delete_all_pvcs: true } : {}),
            ...(isUpgrade && recoveryMode ? { recovery_mode: true } : {}),
            ...(valuesOverride ? { values_override: valuesOverride } : {}),
          },
        })
        onSuccess(op.id)
        onClose()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create operation')
    } finally {
      setIsSubmitting(false)
    }
  }

  const modeDescription = (): string => {
    switch (mode) {
      case 'upgrade':
        return t('pages:environments.edgeDialog.upgradeDescription', 'Installs the Agion Edge runtime stack on the target cluster. This includes agent host, mission runner, MCP server, channel gateway, and intelligence services.')
      case 'reinstall':
        return t('pages:environments.edgeDialog.reinstallDescription', 'This will uninstall the current Edge runtime, clean up all resources, and reinstall from scratch. Use this when the cluster is in a broken state.')
      case 'uninstall':
        return t('pages:environments.edgeDialog.uninstallDescription', 'Removes the Edge runtime entirely. All pods and databases will be deleted from the target cluster. You can reinstall afterwards.')
    }
  }

  const submitLabel = (): string => {
    switch (mode) {
      case 'upgrade':
        return t('pages:environments.edgeDialog.upgrade', 'Upgrade')
      case 'reinstall':
        return t('pages:environments.edgeDialog.reinstall', 'Reinstall')
      case 'uninstall':
        return t('pages:environments.edgeDialog.uninstall', 'Uninstall')
    }
  }

  const updateFirstClassOverride = <K extends keyof FirstClassOverrides>(
    key: K,
    value: FirstClassOverrides[K]
  ) => {
    setFirstClassOverrides(prev => ({ ...prev, [key]: value }))
  }

  const resetField = (key: keyof FirstClassOverrides) => {
    setFirstClassOverrides(prev => ({
      ...prev,
      [key]: key === 'vpnEnabled' ? null : '',
    }))
  }

  const activeOverrides = getActiveOverrides()
  const hasActiveOverrides = activeOverrides.length > 0

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('pages:environments.edgeDialog.title', 'Edge Runtime')}
          </DialogTitle>
          <DialogDescription>
            {modeDescription()}
          </DialogDescription>
        </DialogHeader>
        <div className="py-3 space-y-4">
          {/* Mode selector */}
          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v as EdgeRuntimeMode)}
            orientation="horizontal"
          >
            <RadioGroupItem value="upgrade" label={t('pages:environments.edgeDialog.modeUpgrade', 'Upgrade')} />
            <RadioGroupItem value="reinstall" label={t('pages:environments.edgeDialog.modeReinstall', 'Reinstall')} />
            <RadioGroupItem value="uninstall" label={t('pages:environments.edgeDialog.modeUninstall', 'Uninstall')} />
          </RadioGroup>

          {/* Chart version — not shown for uninstall */}
          {!isUninstall && (
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                {t('pages:environments.installDialog.chartVersion', 'Chart Version')}
              </Label>
              <Select
                value={chartVersion || undefined}
                onValueChange={setChartVersion}
                disabled={isLoadingVersions}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages:environments.installDialog.latestVersion', 'Select version...')} />
                </SelectTrigger>
                <SelectContent>
                  {chartVersions.map(v => (
                    <SelectItem key={v.version} value={v.version}>
                      {v.version} ({v.appVersion})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* SSH key selector — not shown for uninstall */}
          {!isUninstall && needsSshKey && sshKeys.length > 0 && (
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1">
                <Key size={12} />
                {t('pages:environments.installDialog.sshKey', 'SSH Key')}
              </Label>
              <Select value={selectedSshKeyId || undefined} onValueChange={setSelectedSshKeyId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('pages:environments.installDialog.sshKeySelect', 'Select SSH key...')} />
                </SelectTrigger>
                <SelectContent>
                  {sshKeys.map(key => (
                    <SelectItem key={key.id} value={key.id}>
                      {key.name} ({key.keyType})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Reinstall-only: delete PVCs checkbox */}
          {isReinstall && (
            <Label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={deleteAllPvcs}
                onCheckedChange={checked => setDeleteAllPvcs(checked === true)}
                aria-label={t('pages:environments.reinstallDialog.deleteAllPvcs', 'Delete all persistent data')}
              />
              <span className="text-sm text-red-600 dark:text-red-400">
                {t('pages:environments.reinstallDialog.deleteAllPvcs', 'Delete all persistent data')}
              </span>
            </Label>
          )}

          {/* Info text — not shown for uninstall */}
          {!isUninstall && (
            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              {t('pages:environments.installDialog.info', 'A bootstrap token will be generated automatically for cluster registration.')}
            </p>
          )}

          {/* Advanced section — not shown for uninstall */}
          {!isUninstall && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                <ChevronDown size={12} className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                {t('pages:environments.installDialog.advanced', 'Advanced')}
              </Button>
              {showAdvanced && (
                <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-3">
                  {isUpgrade && (
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={recoveryMode}
                        onCheckedChange={checked => setRecoveryMode(checked === true)}
                        aria-label={t('pages:environments.installDialog.recoveryMode', 'Recovery mode')}
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        {t('pages:environments.installDialog.recoveryMode', 'Recovery mode')}
                      </span>
                    </Label>
                  )}

                  {/* First-class override fields */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                      {t('pages:environments.edgeDialog.overrides.title', 'First-Class Overrides')}
                    </h4>

                    {/* Flyway Agion Digest */}
                    <OverrideField
                      id="flyway-agion-digest"
                      label={t('pages:environments.edgeDialog.overrides.flywayAgionDigest', 'Flyway Agion Digest')}
                      description={t('pages:environments.edgeDialog.overrides.flywayAgionDigestDesc', 'Pin flyway-agion to a specific image digest (e.g., sha256:abc123)')}
                      value={firstClassOverrides.flywayAgionDigest}
                      onChange={v => updateFirstClassOverride('flywayAgionDigest', v)}
                      onReset={() => resetField('flywayAgionDigest')}
                      placeholder={t('pages:environments.edgeDialog.overrides.digestPlaceholder', 'sha256:...')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    {/* Flyway Intelligence Digest */}
                    <OverrideField
                      id="flyway-intelligence-digest"
                      label={t('pages:environments.edgeDialog.overrides.flywayIntelligenceDigest', 'Flyway Intelligence Digest')}
                      description={t('pages:environments.edgeDialog.overrides.flywayIntelligenceDigestDesc', 'Pin flyway-intelligence to a specific image digest')}
                      value={firstClassOverrides.flywayIntelligenceDigest}
                      onChange={v => updateFirstClassOverride('flywayIntelligenceDigest', v)}
                      onReset={() => resetField('flywayIntelligenceDigest')}
                      placeholder={t('pages:environments.edgeDialog.overrides.digestPlaceholder', 'sha256:...')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    {/* Binary tags */}
                    <OverrideField
                      id="agent-host-tag"
                      label={t('pages:environments.edgeDialog.overrides.agentHostTag', 'Agent Host Tag')}
                      description={t('pages:environments.edgeDialog.overrides.agentHostTagDesc', 'Override agent-host image tag')}
                      value={firstClassOverrides.agentHostTag}
                      onChange={v => updateFirstClassOverride('agentHostTag', v)}
                      onReset={() => resetField('agentHostTag')}
                      placeholder={t('pages:environments.edgeDialog.overrides.tagPlaceholder', 'v1.0.0')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    <OverrideField
                      id="mission-runner-tag"
                      label={t('pages:environments.edgeDialog.overrides.missionRunnerTag', 'Mission Runner Tag')}
                      description={t('pages:environments.edgeDialog.overrides.missionRunnerTagDesc', 'Override mission-runner image tag')}
                      value={firstClassOverrides.missionRunnerTag}
                      onChange={v => updateFirstClassOverride('missionRunnerTag', v)}
                      onReset={() => resetField('missionRunnerTag')}
                      placeholder={t('pages:environments.edgeDialog.overrides.tagPlaceholder', 'v1.0.0')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    <OverrideField
                      id="agion-mcp-tag"
                      label={t('pages:environments.edgeDialog.overrides.agionMcpTag', 'Agion MCP Tag')}
                      description={t('pages:environments.edgeDialog.overrides.agionMcpTagDesc', 'Override agion-mcp image tag')}
                      value={firstClassOverrides.agionMcpTag}
                      onChange={v => updateFirstClassOverride('agionMcpTag', v)}
                      onReset={() => resetField('agionMcpTag')}
                      placeholder={t('pages:environments.edgeDialog.overrides.tagPlaceholder', 'v1.0.0')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    <OverrideField
                      id="intelligence-zone-tag"
                      label={t('pages:environments.edgeDialog.overrides.intelligenceZoneTag', 'Intelligence Zone Tag')}
                      description={t('pages:environments.edgeDialog.overrides.intelligenceZoneTagDesc', 'Override intelligence-zone image tag')}
                      value={firstClassOverrides.intelligenceZoneTag}
                      onChange={v => updateFirstClassOverride('intelligenceZoneTag', v)}
                      onReset={() => resetField('intelligenceZoneTag')}
                      placeholder={t('pages:environments.edgeDialog.overrides.tagPlaceholder', 'v1.0.0')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    <OverrideField
                      id="intelligence-ingestion-tag"
                      label={t('pages:environments.edgeDialog.overrides.intelligenceIngestionTag', 'Intelligence Ingestion Tag')}
                      description={t('pages:environments.edgeDialog.overrides.intelligenceIngestionTagDesc', 'Override intelligence-ingestion image tag')}
                      value={firstClassOverrides.intelligenceIngestionTag}
                      onChange={v => updateFirstClassOverride('intelligenceIngestionTag', v)}
                      onReset={() => resetField('intelligenceIngestionTag')}
                      placeholder={t('pages:environments.edgeDialog.overrides.tagPlaceholder', 'v1.0.0')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    <OverrideField
                      id="channel-gateway-tag"
                      label={t('pages:environments.edgeDialog.overrides.channelGatewayTag', 'Channel Gateway Tag')}
                      description={t('pages:environments.edgeDialog.overrides.channelGatewayTagDesc', 'Override channel-gateway image tag')}
                      value={firstClassOverrides.channelGatewayTag}
                      onChange={v => updateFirstClassOverride('channelGatewayTag', v)}
                      onReset={() => resetField('channelGatewayTag')}
                      placeholder={t('pages:environments.edgeDialog.overrides.tagPlaceholder', 'v1.0.0')}
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    {/* Replicas */}
                    <OverrideField
                      id="replicas"
                      label={t('pages:environments.edgeDialog.overrides.replicas', 'Replicas')}
                      description={t('pages:environments.edgeDialog.overrides.replicasDesc', 'Number of StatefulSet replicas')}
                      value={firstClassOverrides.replicas}
                      onChange={v => updateFirstClassOverride('replicas', v)}
                      onReset={() => resetField('replicas')}
                      placeholder={t('pages:environments.edgeDialog.overrides.replicasPlaceholder', '1')}
                      type="number"
                      resetLabel={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                    />

                    {/* VPN Enabled */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <Label htmlFor="vpn-enabled" className="text-xs font-medium text-slate-600 dark:text-slate-400 block">
                          {t('pages:environments.edgeDialog.overrides.vpnEnabled', 'VPN Enabled')}
                        </Label>
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">
                          {t('pages:environments.edgeDialog.overrides.vpnEnabledDesc', 'Enable or disable VPN connectivity')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="vpn-enabled"
                          aria-label={t('pages:environments.edgeDialog.overrides.vpnEnabled', 'VPN Enabled')}
                          checked={firstClassOverrides.vpnEnabled === true}
                          onCheckedChange={checked => updateFirstClassOverride('vpnEnabled', checked)}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => resetField('vpnEnabled')}
                          className="h-6 w-6 p-0"
                          aria-label={t('pages:environments.edgeDialog.overrides.resetToDefault', 'Reset to default')}
                        >
                          <RotateCcw size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Preview of active overrides */}
                  {hasActiveOverrides && (
                    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50 dark:bg-slate-800/50">
                      <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                        {t('pages:environments.edgeDialog.overrides.preview', 'Preview')}
                      </h4>
                      <ul className="space-y-1">
                        {activeOverrides.map(({ path, value }) => (
                          <li key={path} className="text-xs font-mono text-slate-700 dark:text-slate-300">
                            <span className="text-blue-600 dark:text-blue-400">{path}</span>
                            <span className="text-slate-500">: </span>
                            <span>{value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Advanced JSON textarea */}
                  <div>
                    <Label htmlFor="values-override" className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1">
                      {t('pages:environments.installDialog.valuesOverride', 'Helm value overrides')}
                    </Label>
                    <Textarea
                      id="values-override"
                      aria-label={t('pages:environments.installDialog.valuesOverride', 'Helm value overrides')}
                      value={valuesOverrideText}
                      onChange={e => handleValuesChange(e.target.value)}
                      placeholder={VALUES_OVERRIDE_PLACEHOLDER}
                      rows={3}
                      className="text-xs font-mono"
                    />
                    {valuesOverrideError && (
                      <p className="text-xs text-red-500 mt-1">{valuesOverrideError}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('pages:environments.installDialog.cancel', 'Cancel')}
          </Button>
          <Button
            variant={isUninstall ? 'destructive' : 'default'}
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || (!isUninstall && !chartVersion) || hasValuesError}
          >
            {isSubmitting
              ? <Spinner className="h-4 w-4 mr-1" />
              : isUninstall
                ? <Trash2 size={14} className="mr-1" />
                : <Rocket size={14} className="mr-1" />}
            {submitLabel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Reusable component for first-class override fields
interface OverrideFieldProps {
  id: string
  label: string
  description: string
  value: string
  onChange: (value: string) => void
  onReset: () => void
  placeholder: string
  type?: 'text' | 'number'
  resetLabel: string
}

function OverrideField({
  id,
  label,
  description,
  value,
  onChange,
  onReset,
  placeholder,
  type = 'text',
  resetLabel,
}: OverrideFieldProps) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <Label htmlFor={id} className="text-xs font-medium text-slate-600 dark:text-slate-400 block">
          {label}
        </Label>
        <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5 mb-1">
          {description}
        </p>
        <Input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="text-xs"
          aria-label={label}
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onReset}
        className="h-6 w-6 p-0 mt-6"
        aria-label={resetLabel}
      >
        <RotateCcw size={12} />
      </Button>
    </div>
  )
}
